import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useOnResize } from 'src/hooks/useOnResize';

import { getCookie, setCookie } from 'src/utils/cookies';
import { cyrb53Hash } from 'src/utils/cyrb53Hash';
import { debounce } from 'src/utils/debounce';

import { ComponentProps } from 'src/types/component-props';
import { CoreAlertBannerData } from 'src/types/components';

import CloseIcon from 'assets/icons/banner/close.svg';

import Text from 'components/BaseHelpers/Text';
import TextCTA from 'components/BaseHelpers/TextCTA';

import AlertIcon from './AlertIcon/AlertIcon';

import styles from './CoreAlertBanner.module.scss';

export type CoreAlertBannerProps = ComponentProps & CoreAlertBannerData;

const formatCookieName = (message: string) => {
  return `alert-banner-${cyrb53Hash(message)}`;
};

const CoreAlertBanner = ({ fields }: CoreAlertBannerProps): JSX.Element => {
  const { active, message, callToAction, heroTheme, heroVariant, bannerType, bannerIcon } = fields;

  const { setOnResizeFn } = useOnResize();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hidden, setHidden] = useState(true);
  const [height, setHeight] = useState(0);

  const updateHeight = debounce(() => {
    const banner = contentRef.current;
    if (banner) {
      const marginProperty = window.getComputedStyle(banner).getPropertyValue('margin').split(' ');
      const verticalMargin = parseInt(marginProperty[0]);
      const rect = banner.getBoundingClientRect();
      const height = rect.height;
      setHeight(height + verticalMargin * 2);
    } else {
      setHeight(0);
    }
  }, 100);

  setOnResizeFn(() => {
    if (!active || hidden) {
      return;
    }
    updateHeight();
  });

  useEffect(() => {
    if (contentRef !== null) {
      updateHeight();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef]);

  const themeModifierClass = classNames(
    styles[`hero-theme--${heroTheme}`],
    styles[`theme--${bannerType}`]
  );
  const heroModifierClass = styles[`hero-variant--${heroVariant}`];

  const modifierClasses = classNames(
    themeModifierClass,
    heroModifierClass,
    styles[`visibility--${hidden ? 'hidden' : 'visible'}`]
  );

  useEffect(() => {
    const hasSeenBanner = getCookie(formatCookieName(message));
    setHidden(Boolean(hasSeenBanner));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissBanner = () => {
    setHidden(true);
    setCookie(formatCookieName(message), 'true', 330);
  };

  if (!active) {
    return <></>;
  }

  const label = bannerType === 'standard' ? bannerIcon ?? 'Alert.' : '';

  return (
    <>
      {/* a div on the same level as the sticky alert, that way it can be positioned behind the main content, and stay there */}
      {/* height is set to 2x, to account for the nav shifting height */}
      <div
        className={classNames(styles.background, modifierClasses)}
        style={{ ['--height']: `${height * 2}px` }}
      ></div>
      <div
        style={{ ['--height']: `${height}px` }}
        className={classNames(styles.main, modifierClasses, 'global__wrapper--1920')}
        role="alertdialog"
        aria-label={label}
        aria-describedby="alert-title"
        aria-labelledby="banner-type"
      >
        <div className={classNames(styles.banner)} ref={contentRef}>
          <div className={styles.innerContainer}>
            <AlertIcon bannerIcon={bannerIcon} bannerType={bannerType} />
            <p id="alert-title" className={styles.copy}>
              <Text field={message} />{' '}
              {callToAction?.href && (
                <TextCTA iconVariant="inherit" tag="a" {...callToAction} className={styles.cta} />
              )}
            </p>
            <button className={styles.closeButton} onClick={dismissBanner} aria-label="Close alert">
              <CloseIcon className={styles.closeIcon} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoreAlertBanner;
